import { faStickyNote } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "@growtherapy/sprout-ui";
import { Popover, PopoverButton } from "@headlessui/react";
import { useFeatureGate } from "@statsig/react-bindings";
import { ComponentProps, FC, forwardRef, useCallback, useRef } from "react";
import { ControlPanelButton } from "../../components";
import { PopoverChevronIndicator } from "../../components/popover/PopoverChevronIndicator";
import { PopoverPanel } from "../../components/popover/PopoverPanel";
import { useKeyPressEvent } from "../../hooks/useKeyPressEvent";
import { scribblesGate } from "../../statsig/gates";
import { capitalize, mergeRefs } from "../../utils";
import { InSessionPatientNoteWrapper as InSessionPatientNote } from "../patient-note";

const PatientNotePopoverButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean }
>(function PatientNotePopoverButton({ isOpen, ...props }, ref) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mergedRefs = mergeRefs<HTMLButtonElement>(ref, buttonRef);

  /**
   * Unfortunately there's no way to programmatically open and close the
   * popover. Our workaround is to "click" the button to toggle it.
   */
  const togglePopover = useCallback(() => {
    buttonRef.current?.click();
  }, []);

  useKeyPressEvent("N", togglePopover);

  const actionName = isOpen ? "close" : "open";

  return (
    <ControlPanelButton
      {...props}
      ref={mergedRefs}
      aria-label={`${capitalize(actionName)} sticky note. Pressing shift + n will also ${actionName} the sticky note.`}
      iconDefinition={faStickyNote}
    >
      <PopoverChevronIndicator isOpen={isOpen} />
    </ControlPanelButton>
  );
});

export type PatientNotePopoverControlButtonProps = {
  patientNoteComponent: FC<ComponentProps<typeof InSessionPatientNote>>;
};

export function PatientNotePopoverControlButton({
  patientNoteComponent: PatientNoteComponent,
}: PatientNotePopoverControlButtonProps) {
  return (
    <Popover>
      {({ open }: { open: boolean }) => (
        <>
          <Tooltip
            text={`${open ? "Close" : "Open"} sticky note (shift + n)`}
            childIsInteractive
          >
            <PopoverButton as={PatientNotePopoverButton} isOpen={open} />
          </Tooltip>
          <PopoverPanel
            anchor={undefined}
            className="min-w-full sm:min-w-[570px] !max-w-[570px]"
          >
            <PatientNoteComponent />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}

export function PatientNotePopoverControlButtonWrapper() {
  const { value: isFeatureEnabled } = useFeatureGate(scribblesGate);

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <PatientNotePopoverControlButton
      patientNoteComponent={InSessionPatientNote}
    />
  );
}
